import React from 'react'
import IconButton from '@material-ui/core/IconButton'
import { DialogTitle } from '@material-ui/core'
import DialogContent from '@material-ui/core/DialogContent'

// @src imports
import { Button, Dialog, Icon, Transition } from 'src/chrome'
import {
  AccountFragment,
  ItemFragment,
  StripeOrderFragment,
} from 'src/legacy_graphql'
import {
  useCallback,
  useEffect,
  useLoading,
  useMutations,
  useState,
} from 'src/hooks'
import {
  ItemsPaymentInfo,
  PurchaseTypes,
} from 'src/payments/containers/Payment'
// relative imports

import {
  ItemsContext,
  PaymentMethod,
  ShippingAddress,
} from 'src/payments/components'
import styles from '../../../payments/containers/styles'
import { Text } from '@sendoutcards/quantum-design-ui'

type Props = {
  onCancel: () => void
  account: AccountFragment
  item: ItemFragment
  onAfterPurchase: () => void
}

const PaymentModal: React.FC<Props> = props => {
  const { onCancel, account, item, onAfterPurchase } = props

  const [error, setError] = useState<string | Error>()
  const [order, setOrder] = useState<StripeOrderFragment>()
  const mutations = useMutations()

  const paymentInfo: ItemsPaymentInfo = {
    items: [item],
    purchaseType: PurchaseTypes.ITEMS,
  }

  const hasAddress = account.shippingAddress && account.shippingAddress.address1
  const purchaseError = hasAddress ? undefined : 'Return Address Required'
  const displayedError =
    (error instanceof Error && error.message) || error || purchaseError
  const purchasing = useLoading()

  const purchase = async () => {
    if (!order) {
      // This shouldn't happen, as the submit button is disabled when
      // the order hasn't been created yet
      return
    }

    purchasing.start()

    try {
      const {
        purchaseProduct: { purchased: didPurchase },
      } = await mutations.purchaseProduct({
        orderId: order.id,
      })
      purchasing.end()
      if (didPurchase) {
        onAfterPurchase()
      }
    } catch {
      purchasing.end()
    }
  }
  const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message
    return String(error)
  }

  const createStripeOrder = useCallback(async () => {
    try {
      const {
        createStripeOrder: { stripeOrder },
      } = await mutations.createStripeOrder({
        itemsToPurchase: [{ itemCode: item.itemCode, quantity: 1 }],
      })
      setOrder(stripeOrder)
      setError(undefined)
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
    }
  }, [item, mutations])

  const createCampaignOrder = useCallback(async () => {
    try {
      const {
        createCampaignOrder: { stripeOrder },
      } = await mutations.createCampaignOrder({
        itemCode: item.itemCode,
      })
      setOrder(stripeOrder)
      setError(undefined)
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
    }
  }, [item, mutations])

  useEffect(() => {
    if (account.shippingAddress && account.shippingAddress.address1) {
      if (item.campaign) {
        createCampaignOrder()
      } else {
        createStripeOrder()
      }
    }
  }, [account, createCampaignOrder, createStripeOrder, item])

  return (
    <Dialog open={true} onClose={onCancel} scroll={'paper'} maxWidth="lg">
      <DialogTitle disableTypography={true}>
        <Text type="body">{'Checkout'}</Text>

        <IconButton
          style={{
            position: 'absolute',
            right: 12,
            top: 6,
          }}
          aria-label="close"
          onClick={onCancel}
        >
          <Icon icon={'CLOSE'} color={'#6f8394'} size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent css={styles.paymentModal} dividers={true}>
        <div style={{ position: 'relative' }}>
          <div css={styles.paymentWrapper}>
            <div css={styles.accountColumn}>
              <ShippingAddress
                account={account}
                isEditable={true}
                title={'Return Address'}
              />

              <div css={styles.sections}>
                <div css={styles.sectionTitle}>
                  <Text type="body">Payment Method</Text>
                </div>
                <PaymentMethod
                  account={account}
                  paymentInfo={paymentInfo}
                  onError={setError}
                  isMixedOrder={false}
                  shouldNotUseExpense={true}
                  canUseCredits={false}
                  isUsingCredits={false}
                  setIsUsingCredits={(value: boolean) => {}}
                />
              </div>
            </div>

            <div css={styles.orderColumn}>
              <ItemsContext items={paymentInfo.items} stripeOrder={order} />
            </div>

            <div css={styles.paymentAction}>
              <Button
                onClick={onCancel}
                style={{ width: 126 }}
                title="Cancel"
              />

              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {displayedError && (
                  <Text type="caption" color="danger" outset={{ right: 'x3' }}>
                    {displayedError}
                  </Text>
                )}

                <Button
                  id={'send_out_card_btn'}
                  title="Buy"
                  buttonColor={purchaseError ? undefined : 'pink'}
                  disabled={
                    !!purchaseError ||
                    !order ||
                    !account.stripeSource ||
                    !account.shippingAddress?.address1
                  }
                  onClick={purchase}
                  style={{ marginLeft: 'auto', width: 126 }}
                />
              </div>
            </div>
          </div>

          {purchasing.isLoading && (
            <Transition
              message={'Purchasing item...'}
              subMessage={undefined}
              style={{
                position: 'absolute',
                height: '100%',
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentModal
